import { Activity, Trash2, Sparkles } from "lucide-react";
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS, fmt } from "@/lib/clinical";

export default function NotesSection({ notes, noteText, setNoteText, saving, onSave, onDelete, isAdmin }) {
  const [polishing, setPolishing] = useState(false);

  const polish = async () => {
    if (!noteText.trim()) return;
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/polish", { text: noteText, context: "ongoing clinical note for a patient treatment record" });
      setNoteText(data.polished);
      toast.success("Polished — check it, then save");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPolishing(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6" data-testid="clinical-notes-section">
      <h2 className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5 text-[#00A499]" /> Ongoing Notes
      </h2>
      <div className="flex flex-col sm:flex-row gap-3 mb-2">
        <textarea
          data-testid="add-clinical-note-input"
          rows={2}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add an ongoing note for this patient…"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
        />
        <div className="flex sm:flex-col gap-2">
          <button onClick={onSave} disabled={saving || !noteText.trim()} data-testid="save-clinical-note-button"
            className="h-12 sm:h-auto sm:flex-1 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-50">
            Add note
          </button>
          <button onClick={polish} disabled={polishing || !noteText.trim()} data-testid="ai-polish-note-button"
            title="AI: rewrites your rough note into professional wording — same facts, just better written"
            className="h-12 sm:h-auto sm:flex-1 px-4 rounded-lg border border-[#7C3AED]/40 text-[#7C3AED] hover:bg-violet-50 transition-colors text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
            <Sparkles className="h-4 w-4" /> {polishing ? "Polishing…" : "AI polish"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Tip: dictate with your phone keyboard's mic, then tap AI polish to make it read professionally. Facts are never changed.</p>
      {notes.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center" data-testid="no-notes-message">No notes yet.</p>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl border border-slate-200 p-4" data-testid={`note-entry-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-slate-700 whitespace-pre-wrap flex-1">{n.text}</p>
                {isAdmin && (
                  <button onClick={() => onDelete(n.id)} data-testid={`delete-note-button-${n.id}`}
                    className="text-[#D5281B] hover:bg-red-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                    title="Delete note (admin only)">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                <span className="font-semibold text-[#0F2547]">{n.author_name}</span>
                <span className="uppercase tracking-wide font-bold text-[#005EB8]">{ROLE_LABELS[n.author_role] || n.author_role}</span>
                <span>· {fmt(n.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
